import { FC } from 'react';

import { RelativeTime } from '@bonusly/common-relative-time';
import { Avatar, Box, HStack, Spacer, StyleProps, Text, Tooltip, VStack } from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';

import { sanitize } from '../../../../hooks/use-sanitize-rich-text';
import { GoalStatus } from '../../../../types/goal';
import { parseDateSafely } from '../../../../utils/date';
import { getStatusLabel } from '../goals/goal-status-badge';
import { ReferenceUnlinkButton } from '../reference-unlink-button/reference-unlink-button';

export interface GoalUpdate {
	id: string;
	userId: string;
	content: string;
	source: string;
	createdAt: string;
	updatedAt?: string;
}

export interface GoalUpdateCardProps extends StyleProps {
	goalUpdate: GoalUpdate;
	onUnlinkUpdate?: (updateId: string) => void;
	sectionTitle?: string;
	onClick?: () => void;
}

const wrapStatusWords = (content: string): string => {
	// Sanitize FIRST to strip XSS payloads, then wrap status words
	const sanitizedContent = sanitize(content);
	return sanitizedContent.replace(
		/\b(not_started|on_track|off_track|at_risk|completed)\b/gi,
		(match) => `<span style="font-weight: 600;">${getStatusLabel(match as GoalStatus)}</span>`
	);
};

export const GoalUpdateCard: FC<GoalUpdateCardProps> = ({ goalUpdate, onUnlinkUpdate, sectionTitle, onClick, ...rest }) => {
	const { data: user } = useUser(goalUpdate.userId);

	if (!goalUpdate) return null;

	if (goalUpdate.source === 'system') {
		// simplified card for system updates (e.g. goal status changes)
		return (
			<Box bg="secondary.bgAlpha" p={3} borderRadius="md" data-component="goal-details-timeline-system-update-card" {...rest}>
				<Text fontSize="sm" color="text.soft" whiteSpace="pre-wrap">
					<Text as="span" fontWeight="semibold" fontSize="sm" color="text.soft">
						{user?.displayName + ' '}
					</Text>
					<Text as="span" fontSize="sm" color="text.soft" dangerouslySetInnerHTML={{ __html: wrapStatusWords(goalUpdate.content) }} />
				</Text>
			</Box>
		);
	}

	return (
		<Box
			bg="bg.surface"
			border="1px solid"
			borderColor="border.default"
			borderRadius="md"
			cursor={onClick ? 'pointer' : 'default'}
			data-component="goal-details-timeline-update-card"
			p={3}
			sx={{ breakInside: 'avoid' }}
			onClick={(e) => {
				// Don't trigger onClick if clicking on interactive elements
				const target = e.target as HTMLElement;
				if (
					target.closest('button') ||
					target.closest('a') ||
					target.closest('[role=\"button\"]') ||
					target.closest('[role=\"menuitem\"]') ||
					target.closest('[role=\"option\"]')
				) {
					return;
				}
				onClick?.();
			}}
			{...rest}
		>
			<VStack alignItems="flex-start">
				<HStack alignItems="center" width="full">
					<Box bg="secondary.bgAlpha" borderRadius="full" p={2} display="flex" alignItems="center" justifyContent="center" w={8} h={8}>
						<Tooltip label={`${user?.displayName}`} placement="top">
							<Avatar src={user?.profilePic} name={user?.displayName} size="sm" border="none" />
						</Tooltip>
					</Box>

					<Text size="sm" fontWeight="semibold" color="text.default">
						{user?.displayName}
					</Text>

					<Spacer />

					<RelativeTime color="text.softest" date={parseDateSafely(goalUpdate.createdAt)} fontSize="sm" />

					{onUnlinkUpdate && sectionTitle && <ReferenceUnlinkButton onClickUnlinkReference={() => onUnlinkUpdate(goalUpdate.id)} sectionTitle={sectionTitle} />}
				</HStack>

				<Text fontSize="sm" color="text.soft" fontWeight="normal" whiteSpace="pre-wrap">
					<Text as="span" color="text.soft" fontSize="sm" dangerouslySetInnerHTML={{ __html: wrapStatusWords(goalUpdate.content) }} />
				</Text>
			</VStack>
		</Box>
	);
};
